<template>
  <content-subpage
    :visible="visible"
    :has-parent-subpage="hasParentSubpage"
    :titles="titles"
    :content-title="contentTitle"
    :back-label="definedBackLabel"
    :show-tooltip="showTooltip && !params.createNewAssignment"
    :tooltip-message="tooltipMessage"
    class="add-from-starred-resources-content-subpage"
    @content-subpage-hidden="actionHidden"
    @content-subpage-back="actionBack"
  >
    <template #main-content>
      <overlay-loader v-if="isProcessing.data" />
      <starred-resource-search-filter :pre-filter="preFilter" @search="handleSearchFilterChanged" />

      <starred-resource-card-list
        :resource-type="params.resourceType"
        :add-from-type="params.addFromType"
        :resource-data="currentPageDisplayData"
        :mode="params.mode"
        :titles="titles"
        :add-from-location="params.addFromLocation"
        :total-records="totalRecords"
        :create-new-assignment="params.createNewAssignment"
        :is-from-planner="params.isFromPlanner"
        :is-attaching-attachment="params.isAttachingAttachment"
        :selection-mode="params.selectionMode"
        :add-from-mpq-child-layer="params.addFromMpqChildLayer"
        :add-from-mpq-sub-layer="params.addFromMpqSubLayer"
        :creation-params="clonedCreationParams"
        :max-page-size="maxPageSize"
        :current-page="pageNumber"
        :search-filters="filters"
        :has-starred-resources="hasStarredResources"
        @handle-selected="handleSelected"
        @page-change="handlePageChanged"
        @selection-changed="handleSelectionChanged"
        @selection-saved="handleSelectionSaved"
        @resource-unstarred="handleResourceUnstarred"
        @selected="handleItemSelected"
      />
    </template>
    <template v-if="showActionButton" #action-button>
      <fetch-data-button
        class="add"
        :kind="dataChooseButton.kind"
        :error="dataChooseButton.error"
        :error-message="dataChooseButton.errorMessage"
        :size="dataChooseButton.size"
        :loading="dataChooseButton.loading"
        :disabled="dataChooseButton.loading || dataChooseButton.error || isDisabled"
        :tip-position="dataChooseButton.tipPosition"
        :tip-alignment="dataChooseButton.tipAlignment"
        @click="handleSelect"
        @reset-error-state="handleChooseButtonResetError"
        @reset-completed-state="handleChooseButtonResetCompleted"
      >
        <template v-if="!dataChooseButton.error">
          <svg-icon name="Proceed32" kind="action" />
          <span>{{ t("button_select") }}</span>
        </template>
      </fetch-data-button>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
// 1. Imports
import StarredResourceSearchFilter from "@/plugins/starred-resource-listing/components/StarredResourceSearchFilter.vue";
import StarResourceApi from "@apis/star.resource.api";
import EditorAddExistingResourceMixin from "@modules/editor/mixin/editor.add.existing.resource.mixin";
import EditorNavigationMixin from "@modules/editor/mixin/editor.navigation.mixin";
import {
  ADD_FROM_EXISTING_TYPE,
  NAMESPACE,
  STARRED_RESOURCES_PAGE_SIZE,
} from "@modules/editor/service/editor.constant";
import SearchStore from "@modules/search/components/SearchStore";
import {
  RESOURCE_DOMAIN,
  RESOURCE_FILTER_OPTIONS,
} from "@modules/starred-resource/service/starred.resource.constant";
import { ResourceFavourite } from "@modules/starred-resource/starred-resource-types.ts";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import OverlayLoader from "@plugins/common/components/OverlayLoader.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { useErrorHandler } from "@plugins/common/composables/error";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import {
  ContentSubpageParams,
  useContentSubpage,
} from "@plugins/content-subpage/composables/content-subpage";
import { isDefined, isEmpty } from "@utils/var.check";
import { useVm } from "@utils/vm.ts";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import StarredResourceCardList from "./StarredResourceCardList.vue";
import { AddLessonFromLibraryContentSubpageSelectionEvent } from "@modules/resource-search/resource-search-types.ts";
import type { LessonSelectionMode } from "@modules/resource-search/resource-search-types.ts";

// 2. Define options
defineOptions({
  name: "AddFromStarredResourcesContentSubpage",
  mixins: [EditorNavigationMixin, EditorAddExistingResourceMixin],
});

// 3. Props declaration
interface Favourite {
  uuid: string;
  title: string;
  resourceType: string;
  resourceDomain: string;
  questionType?: string;
  fileType?: string;
  starredDate: string;
  createdDate: string;
}

interface Filter {
  resource?: string;
  resourceTypes?: string[];
  searchKeyword?: string;
}

interface CreationParams {
  addResourceActionBack?: () => void;
  addResourceActionBackLabel?: string;
  addResourceActionBackParams?: object | never;
}

interface Params extends ContentSubpageParams {
  resourceType: string;
  addFromType: string;
  mode: string;
  addFromLocation: string;
  isFromPlanner?: boolean;
  isAttachingAttachment?: boolean;
  selectionMode?: LessonSelectionMode;
  addFromMpqChildLayer?: boolean;
  addFromMpqSubLayer?: boolean;
  creationParams?: CreationParams;
  createNewAssignment: boolean;
  studentGroupName: string;
  subpageParentTitle?: string[];
}

interface Props {
  params: Params; // Required to pass header, resourceType, displayMode, preFilter
}

const props = defineProps<Props>();

// 4. Component injects / composables
const { t } = useI18n();
const store = useStore();
const dirtyId = ref("AddFromStarredResources");
const { passiveApiFailHandler } = useErrorHandler();
const {
  visible,
  titles,
  hasParentSubpage,
  actionHidden,
  actionBack,
  isComponentDirty,
  removeDirty,
  setDirty,
  defaultExpose,
} = useContentSubpage({ id: dirtyId.value, ...props.params }, dirtyId);
const { vm, vmExpose } = useVm();

const emit = defineEmits<{
  selected: [data: AddLessonFromLibraryContentSubpageSelectionEvent];
}>();

// 5. Refs & reactive state
const parentSubpageName = ref("");
const isProcessing = ref({ data: true });
const searchStore = ref(SearchStore);
const dataChooseButton = ref({
  kind: "primary" as const,
  size: "small" as const,
  loading: false,
  error: false,
  tipPosition: "top" as const,
  tipAlignment: "end" as const,
  errorMessage: "",
});
const maxPageSize = ref(STARRED_RESOURCES_PAGE_SIZE);
const pageNumber = ref(1);
const resourceFavourites = ref<ResourceFavourite[]>([]);
const resourceFavouritesListData = ref<Favourite[]>([]);
const preFilter = ref<object | never>({});
const filters = ref<Filter>({});
const contentTitle = ref(
  props.params.isFromPlanner ? t("calendar_event_attachment_add_from_starred_resources_label") : "",
);
const clonedCreationParams = ref<CreationParams>();

// 6. Computed properties
// possible for `lesson` to be undefined.
const lesson = computed(() => store.getters[`${NAMESPACE.LESSON}/getLesson`]);
const hasStarredResources = computed(() => resourceFavourites.value?.length > 0);

const tooltipMessage = computed(() => {
  if (!vm.showTooltip) return "";
  if (props.params.addFromType === ADD_FROM_EXISTING_TYPE.ACTIVITY) {
    return t("add_activity_from_starred_resources_subpage_title_tooltip");
  }
  return "";
});

const definedBackLabel = computed(() =>
  !isEmpty(props.params.backLabelFromParent) ? props.params.backLabelFromParent : vm.backLabel,
);

const filteredResourceFavouritesListData = computed(() => {
  if (isEmpty(resourceFavouritesListData.value)) {
    return [];
  }

  let filteredData = [...resourceFavouritesListData.value];

  // resource filter
  if (isDefined(filters.value?.resource)) {
    filteredData = filteredData.filter((item) => item.resourceType === filters.value.resource);
  }

  if (isDefined(filters.value?.resource)) {
    filteredData = filteredData.filter(
      (item) =>
        item.resourceType === filters.value.resource &&
        !(
          filters.value.resource === RESOURCE_FILTER_OPTIONS.LESSON.value &&
          item.resourceDomain === RESOURCE_DOMAIN.ALP_TEMPLATE
        ), // SLS-8593 - remove star for ALP Template
    );
  }

  // resource type filter
  if (isDefined(filters.value?.resourceTypes)) {
    if (filters.value.resource === RESOURCE_FILTER_OPTIONS.QUESTION.value) {
      filteredData = filteredData.filter(
        (item) =>
          filters.value.resourceTypes &&
          filters.value.resourceTypes.includes(item.questionType ?? ""),
      );
    } else if (filters.value.resource === RESOURCE_FILTER_OPTIONS.MEDIA.value) {
      filteredData = filteredData.filter(
        (item) =>
          filters.value.resourceTypes && filters.value.resourceTypes.includes(item.fileType ?? ""),
      );

      if (props.params.imageThumbnail) {
        const resourceDomains = [RESOURCE_DOMAIN.PERSONAL, RESOURCE_DOMAIN.COMMUNITY];
        filteredData = filteredData.filter((item) => resourceDomains.includes(item.resourceDomain));
      }
    }
  }

  // search keyword
  if (!isEmpty(filters.value.searchKeyword)) {
    const keyword = filters.value.searchKeyword ?? "";
    filteredData = filteredData.filter((item) =>
      item.title.toLowerCase().includes(keyword.toLowerCase()),
    );
  }

  return filteredData;
});

const currentPageDisplayData = computed(() => {
  const dataStartIndex = (pageNumber.value - 1) * maxPageSize.value;
  return filteredResourceFavouritesListData.value.slice(
    dataStartIndex,
    dataStartIndex + maxPageSize.value,
  );
});

const totalRecords = computed(() => filteredResourceFavouritesListData.value?.length ?? 0);

// 7. Methods / event handlers
const handleSearchFilterChanged = (filtersData: object | never): void => {
  filters.value = filtersData;
};

const fetchStarredResourceListing = async () => {
  try {
    const response = await StarResourceApi.fetchStarredResourcesByType({
      resourceType: props.params.resourceType,
    });
    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }
    resourceFavourites.value = response.data;

    if (resourceFavourites.value.length > 0) {
      generateResourceData();
    }

    isProcessing.value.data = false;
  } catch (error) {
    isProcessing.value.data = false;
    passiveApiFailHandler(error);
  }
};

const handleResourceUnstarred = (uuid) => {
  resourceFavouritesListData.value = resourceFavouritesListData.value.filter(
    (item) => item.uuid !== uuid,
  );
  searchStore.value.removeSelectionList(uuid);
};

const handleItemSelected = (data: AddLessonFromLibraryContentSubpageSelectionEvent) => {
  if (data.onSuccess) {
    const childOnSuccess = data.onSuccess;
    data.onSuccess = (backCurrentSubpage) => {
      childOnSuccess(true);
      const shouldBackCurrentSubpage =
        backCurrentSubpage || Boolean(props.params.isAttachingAttachment);
      if (!shouldBackCurrentSubpage) {
        return;
      }
      nextTick(() => {
        actionBack();
      });
    };
  }
  emit("selected", data);
};

const handlePageChanged = (pageNum: number): void => {
  pageNumber.value = pageNum;
};

const generateResourceData = (): void => {
  resourceFavouritesListData.value = resourceFavourites.value.map((item) => ({
    ...item,
    starredDate: item.createdDate,
  }));
};

const initDefaultFilters = (): void => {
  preFilter.value = {
    resourceType: props.params.resourceType,
    questionTypes: props.params.questionTypes,
    mediaTypes: props.params.mediaTypes,
  };
};

// 8. Lifecycle hooks
onMounted(() => {
  if (hasParentSubpage.value) {
    parentSubpageName.value = titles.value.slice(-1)[0];
    const subTitle = t("add_from_library_subpage_title_label", {
      addFromType: vm.addFromTypeLabel,
      addFromLocation: vm.addFromLocationLabel,
    });

    if (props.params.isFromPlanner) {
      titles.value.push(t("calendar_event_attachment_attach_resource_label"));
    } else {
      titles.value.push(subTitle);
    }
  } else if (lesson.value) {
    titles.value.push(lesson.value.title);
    if (props.params.subpageParentTitle) {
      titles.value.push(...props.params.subpageParentTitle);
    }
    titles.value.push(t("activity_add_component_button_add_from_starred_resources"));
  } else if (props.params.isFromPlanner) {
    titles.value.push(t("calendar_event_attachment_attach_resource_label"));
  } else {
    if (props.params.studentGroupName) {
      titles.value.push(props.params.studentGroupName);
    }
    titles.value.push(t("activity_add_component_button_add_from_starred_resources"));
  }

  const original = props.params.creationParams ?? {};
  let addResourceActionBack = original.addResourceActionBack;
  if (props.params?.creationParams) {
    const actionBackFunc = props.params.creationParams.addResourceActionBack;
    addResourceActionBack = () => {
      actionBack();
      actionBackFunc?.();
    };
  }
  clonedCreationParams.value = {
    ...props.params.creationParams,
    addResourceActionBack: addResourceActionBack,
  };

  initDefaultFilters();
  fetchStarredResourceListing();
});

onBeforeUnmount(() => {
  searchStore.value.destroy();
});

vmExpose({
  dirtyId,
  store: searchStore,
  setDirty,
  removeDirty,
  isComponentDirty,
  isProcessing,
  actionHidden,
  actionBack,
  parentSubpageName, // for EditorAddExistingResourceMixin
});
defineExpose(defaultExpose);
</script>
